import React, { useMemo, useState, useEffect, useId } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Typography, Image, Tag, Modal } from "antd";
import { BookOutlined, FilePdfOutlined, DownloadOutlined, ZoomInOutlined } from "@ant-design/icons";
import { parseDictionary } from "./dictionaryUtils.js";
import { extractSvgLabels, matchDictionaryTerms } from "./svgTermMatch.js";

const { Title, Paragraph, Text } = Typography;

// Full GFM markdown renderer for bimframework description.md content — tables, code spans,
// blockquotes, nested headings — used wherever the simpler bold/italic-only renderer
// (markdownInline.js, still used by KeyboardShortcutsView's own bespoke layout) isn't enough.
// Two things beyond plain rendering:
//
// 1. Images whose filename (minus extension) exactly matches a heading's text, case-
//    insensitively, are embedded inline right after that heading instead of listed in the flat
//    gallery below — e.g. a folder with "## Foo" and "Foo.svg" gets Foo.svg shown under "Foo"
//    automatically, no code change needed for the next section that follows this convention.
//    Anything left over still renders as the plain stacked gallery, same as before.
// 2. SVG diagrams (Visio exports) render with no card at all — no border, transparent
//    background — instead of the bordered/tinted frame used for photo/screenshot images, so
//    the diagram sits directly on the page. Any black text/lines in these exports always
//    carries its own light backing shape as part of the design (the user's own Visio
//    convention), so this is safe to do without risking legibility.
// 3. SVGs are fetched and injected as inline markup (InlineSvgDiagram below), not shown via a
//    plain <img src>. Some mobile browsers decode an <img>-referenced SVG once and treat it as
//    a static raster from then on, silently dropping any CSS/SMIL animation authored inside the
//    file itself (e.g. the Overview schema's "Animation_Fade" Visio layer) even though desktop
//    plays it fine — inline markup is just regular DOM content, so animations run the same on
//    every device. This also means the diagram's own text/labels are fetched once and reused
//    for both display and Dictionary cross-referencing, instead of two separate requests for
//    the same file.

const slugify = (text) =>
  text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");

// Injects "![heading](imageUrl)" right after any heading line whose text matches an image's
// filename stem — a plain string-level pass, not a remark plugin, so react-markdown's own
// parser still does all the real work afterward.
function embedMatchingImages(text, images, imageUrlFor) {
  const byStem = new Map();
  for (const img of images) {
    const stem = img.name.replace(/\.[^.]+$/, "").trim().toLowerCase();
    byStem.set(stem, img);
  }
  const usedNames = new Set();
  const lines = (text || "").split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    out.push(line);
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      const img = byStem.get(heading[1].trim().toLowerCase());
      if (img) {
        usedNames.add(img.name);
        out.push("", `![${heading[1].trim()}](${imageUrlFor(img)})`, "");
      }
    }
  }
  return { text: out.join("\n"), usedNames };
}

// Drops a single leading "# Title" line — the page's own header (BIMFramework/index.js's
// sectionTitle) already shows it, repeating it in the body would be redundant.
function dropLeadingH1(text) {
  return text.replace(/^\s*#\s+.*(\r?\n)+/, "");
}

// Design Standarts' description.md writes a plain "## Disciplines" heading and expects a live,
// clickable list of every discipline's Symbology section to appear under it — not hand-authored
// links, since new disciplines should show up automatically (see getDisciplineLinks in
// index.js). Same string-level heading-match approach as embedMatchingImages: swap a marker
// paragraph in right after the heading, then intercept that exact paragraph text in the `p`
// component override below and render DisciplineLinks instead of prose.
const DISCIPLINE_LINKS_MARKER = "[[DISCIPLINE_LINKS]]";
function embedDisciplineLinksMarker(text) {
  const lines = (text || "").split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    out.push(line);
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading && heading[1].trim().toLowerCase().replace(/:+$/, "") === "disciplines") {
      out.push("", DISCIPLINE_LINKS_MARKER, "");
    }
  }
  return out.join("\n");
}

function isSvgSrc(src) {
  return /\.svg(\?|$)/i.test(src || "");
}

// Manual per-image display-width overrides. Auto-embedded images (see embedMatchingImages
// above) are keyed by their alt text (the matched heading's exact text); flat-gallery images
// (no matching heading — e.g. Overview's SVG, which has no description.md at all) use their
// filename instead, so widthFor() strips a trailing image extension before looking up, letting
// one map serve both without caring which path an image came through. One-off sizing tweaks
// requested for specific diagrams; not a general authoring convention, so this stays a small
// lookup rather than inventing a markdown-level size syntax nobody's asked for yet.
const IMAGE_WIDTH_OVERRIDES = {
  "derivation of superelevation from aashto method 5 variables": "75%",
  "schema-gears": "94%",
};
const widthFor = (alt) => {
  const key = (alt || "").trim().toLowerCase().replace(/\.(svg|png|jpe?g|webp|gif)$/, "");
  return IMAGE_WIDTH_OVERRIDES[key] || "100%";
};

// Pure presentational — the actual term matches now come from InlineSvgDiagram's own fetch
// (see below), not a separate one this component used to make on its own.
function RelatedTermsList({ matches, onNavigate }) {
  if (!matches || matches.length === 0) return null;
  return (
    <div className="md-related-terms">
      <Text className="md-related-label"><BookOutlined /> Related terms</Text>
      {matches.map((m) => (
        <Tag key={m.term} className="md-related-tag" onClick={() => onNavigate(m.term)}>
          {m.term}
        </Tag>
      ))}
    </div>
  );
}

// Strips the width/height off just the root <svg ...> tag (Visio exports these in absolute
// inches) so the injected element sizes itself from CSS (.md-inline-svg svg below) instead of
// rendering at its literal print dimensions — viewBox is left untouched, so the intrinsic
// aspect ratio (and therefore height:auto) still comes out correct. Regex runs against the
// tag substring only, not the whole document, so it can't touch any other element's attributes.
function stripSvgRootSize(svgText) {
  return svgText.replace(/<svg\b[^>]*>/, (openTag) =>
    openTag.replace(/\swidth="[^"]*"/, "").replace(/\sheight="[^"]*"/, "")
  );
}

// Visio never emits <script> in these exports, but this is the site owner's own authored file
// going into dangerouslySetInnerHTML, not user-uploaded content — stripping script tags before
// injection is cheap insurance, not a response to any known issue.
function sanitizeSvgMarkup(svgText) {
  return svgText.replace(/<script[\s\S]*?<\/script>/gi, "");
}

// Every Visio export names its own ids/classes assuming it'll be the only SVG in the document —
// true when opened standalone, false once rendered inline alongside a sibling diagram (e.g.
// Superelevation's two AASHTO derivation SVGs on the same page). Confirmed a real bug this
// caused: both files define a ".st6" class with a *different* `marker-end` target, so whichever
// <style> block landed later in the shared DOM won that class name for both diagrams — lines in
// one diagram ended up pointing at the other diagram's arrowhead marker (or an unrelated rule
// entirely), producing arrows that don't exist in either source file on its own. Prefixing every
// id and every Visio "stN" class with a prefix unique to this mounted instance makes that
// collision impossible regardless of which two files happen to land on the same page together.
function scopeSvgMarkup(svgText, instanceId) {
  const ids = new Set();
  for (const m of svgText.matchAll(/\bid="([^"]+)"/g)) ids.add(m[1]);

  let out = svgText;
  if (ids.size > 0) {
    const idPattern = [...ids].map((id) => id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    // References ("#id" inside url(#id) / xlink:href="#id") first, then the id="..." definitions
    // themselves — doing references first avoids any ambiguity between the two.
    out = out.replace(new RegExp(`#(${idPattern})\\b`, "g"), `#${instanceId}-$1`);
    out = out.replace(/\bid="([^"]+)"/g, (m, id) => (ids.has(id) ? `id="${instanceId}-${id}"` : m));
  }
  // Visio's own per-file class naming convention is exclusively "stN" (confirmed across every
  // export in this project) — a plain word-boundary rename is safe here: it can't touch any
  // other token, and covers both the <style> block's ".stN" selectors and elements' class="stN".
  return out.replace(/\bst(\d+)\b/g, `${instanceId}-st$1`);
}

// These A3-sized Visio drawings scaled down to page width read fine as a diagram but too small
// for their own label text — click (the diagram itself, or the small corner icon) opens the
// same markup much larger in a Modal. Reusing the SAME already-scoped markup for both the small
// and expanded copies would put two elements with the same id in the DOM at once the moment the
// modal opens (exactly the cross-diagram collision scopeSvgMarkup() exists to prevent — just
// intra-instance instead of inter-instance this time), so the "cleaned" markup (sanitized +
// size-stripped, not yet id/class-scoped) is kept in state and re-scoped with a *second*,
// independent instance id for the modal, computed only while it's actually open.
function SvgExpandModal({ cleanedMarkup, instanceId, onClose }) {
  const markup = useMemo(() => scopeSvgMarkup(cleanedMarkup, instanceId), [cleanedMarkup, instanceId]);
  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width="min(94vw, 1400px)"
      centered
      styles={{
        content: { background: "#0d0f12", padding: "28px 32px" },
        body: { padding: 0 },
      }}
    >
      <div className="md-inline-svg" dangerouslySetInnerHTML={{ __html: markup }} />
    </Modal>
  );
}

// Fetches an SVG's raw markup once and injects it as real DOM content — required for any CSS
// animation authored inside the file (see the header comment above) to survive on mobile, and
// lets the same fetched text double as the source for Dictionary term matching, instead of
// RelatedTerms making its own separate request for the same file.
function InlineSvgDiagram({ src, dictionaryTerms, onNavigateToDictionaryTerm }) {
  const instanceId = "svg" + useId().replace(/[^a-zA-Z0-9]/g, "");
  const modalInstanceId = "svg" + useId().replace(/[^a-zA-Z0-9]/g, "");
  const [cleanedMarkup, setCleanedMarkup] = useState(null); // sanitized + size-stripped, not yet id/class-scoped
  const [matches, setMatches] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(src)
      .then((r) => r.text())
      .then((raw) => {
        if (cancelled) return;
        setCleanedMarkup(stripSvgRootSize(sanitizeSvgMarkup(raw)));
        if (dictionaryTerms && dictionaryTerms.length > 0 && onNavigateToDictionaryTerm) {
          setMatches(matchDictionaryTerms(extractSvgLabels(raw), dictionaryTerms));
        }
      })
      .catch(() => { if (!cancelled) setCleanedMarkup(""); });
    return () => { cancelled = true; };
  }, [src, dictionaryTerms, onNavigateToDictionaryTerm]);

  const markup = useMemo(
    () => (cleanedMarkup ? scopeSvgMarkup(cleanedMarkup, instanceId) : ""),
    [cleanedMarkup, instanceId]
  );

  return (
    <div>
      <div className="md-inline-svg-wrap">
        <div
          className="md-inline-svg md-inline-svg-clickable"
          dangerouslySetInnerHTML={{ __html: markup }}
          onClick={() => markup && setExpanded(true)}
        />
        {markup && (
          <button
            type="button"
            className="md-inline-svg-expand"
            onClick={() => setExpanded(true)}
            aria-label="Expand diagram"
          >
            <ZoomInOutlined />
          </button>
        )}
      </div>
      <RelatedTermsList matches={matches} onNavigate={onNavigateToDictionaryTerm} />
      {expanded && (
        <SvgExpandModal
          cleanedMarkup={cleanedMarkup}
          instanceId={modalInstanceId}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
}

// Renders the live discipline list in place of the DISCIPLINE_LINKS_MARKER paragraph. Each tag
// jumps straight to that discipline's own Symbology section (a real left-nav navigation, same
// mechanism as clicking the tree itself) rather than just linking text — "Design Standarts"
// stays the single place a BEP reviewer reads about symbology conceptually, while the actual
// interactive Common/Rail/Road tree stays owned by its discipline, not duplicated here.
function DisciplineLinks({ links, onNavigate }) {
  if (!links || links.length === 0) {
    return (
      <div className="md-discipline-links">
        <Text style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>
          No disciplines published yet.
        </Text>
      </div>
    );
  }
  return (
    <div className="md-discipline-links">
      {links.map((d) => (
        <Tag key={d.key} className="md-related-tag" onClick={() => onNavigate(d.key)}>
          {d.label} <span style={{ opacity: 0.55 }}>→ Symbology</span>
        </Tag>
      ))}
    </div>
  );
}

function makeComponents({ dictionaryTerms, onNavigateToDictionaryTerm, disciplineLinks, onNavigateToSection }) {
  return {
    h1: ({ children }) => <Title level={4} style={{ marginTop: 24 }}>{children}</Title>,
    h2: ({ children }) => <Title level={4} id={slugify(String(children))} style={{ marginTop: 28 }}>{children}</Title>,
    h3: ({ children }) => <Title level={5} id={slugify(String(children))} style={{ marginTop: 22 }}>{children}</Title>,
    h4: ({ children }) => <Title level={5} id={slugify(String(children))} style={{ marginTop: 18, fontSize: 14.5, color: "rgba(255,255,255,0.85)" }}>{children}</Title>,
    p: ({ children }) => {
      if (String(children).trim() === DISCIPLINE_LINKS_MARKER) {
        return <DisciplineLinks links={disciplineLinks} onNavigate={onNavigateToSection} />;
      }
      return <Paragraph style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, lineHeight: 1.7 }}>{children}</Paragraph>;
    },
    a: ({ href, children }) => {
      const external = /^https?:\/\//i.test(href || "");
      return (
        <a href={href} style={{ color: "#4f9cf9" }} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
          {children}
        </a>
      );
    },
    // react-markdown v9+ no longer passes an `inline` boolean to this override (checked the
    // installed v10 source directly — confirmed nothing sets it), so inline vs. fenced-block
    // can't be told apart from here. Current bimframework content only ever uses inline code
    // spans (parameter names in prose/tables), never ``` fenced blocks, so this always renders
    // the inline pill style; `pre` below covers the fenced-block case if one ever shows up.
    code: ({ children }) => <code className="md-inline-code">{children}</code>,
    pre: ({ children }) => <pre className="md-code-block">{children}</pre>,
    blockquote: ({ children }) => <div className="md-callout">{children}</div>,
    table: ({ children }) => <div className="md-table-wrap"><table className="md-table">{children}</table></div>,
    img: ({ src, alt }) => {
      if (isSvgSrc(src)) {
        return (
          <div style={{ width: widthFor(alt), marginLeft: "auto", marginRight: "auto" }}>
            <InlineSvgDiagram src={src} dictionaryTerms={dictionaryTerms} onNavigateToDictionaryTerm={onNavigateToDictionaryTerm} />
          </div>
        );
      }
      return (
        <div style={{ width: widthFor(alt), marginLeft: "auto", marginRight: "auto" }}>
          <div className="md-image-frame">
            <Image src={src} alt={alt} style={{ width: "100%", display: "block" }} />
          </div>
        </div>
      );
    },
  };
}

export default function MarkdownContent({
  text, images = [], files = [], imageUrlFor, downloadUrlFor,
  onNavigateToDictionaryTerm, disciplineLinks, onNavigateToSection,
}) {
  const { processedText, usedNames } = useMemo(() => {
    const stripped = embedDisciplineLinksMarker(dropLeadingH1(text || ""));
    const { text: withImages, usedNames } = embedMatchingImages(stripped, images, imageUrlFor);
    return { processedText: withImages, usedNames };
  }, [text, images, imageUrlFor]);

  // Fetched once per mount, not per image — every SVG on the page cross-references against the
  // same term list, no reason to re-fetch Dictionary's content once per diagram.
  const [dictionaryTerms, setDictionaryTerms] = useState(null);
  useEffect(() => {
    fetch("/api/bimframework/content/Dictionary")
      .then((r) => r.json())
      .then((d) => setDictionaryTerms(parseDictionary(d.description)))
      .catch(() => setDictionaryTerms([]));
  }, []);

  const remainingImages = images.filter((img) => !usedNames.has(img.name));
  const components = useMemo(
    () => makeComponents({ dictionaryTerms, onNavigateToDictionaryTerm, disciplineLinks, onNavigateToSection }),
    [dictionaryTerms, onNavigateToDictionaryTerm, disciplineLinks, onNavigateToSection]
  );

  return (
    <div>
      <style>{`
        .md-inline-code {
          font-family: 'DM Mono', monospace;
          font-size: 12.5px;
          background: rgba(79,156,249,0.12);
          color: #7ab8ff;
          padding: 1px 6px;
          border-radius: 4px;
        }
        .md-code-block {
          font-family: 'DM Mono', monospace;
          font-size: 12.5px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 8px;
          padding: 14px 16px;
          overflow-x: auto;
        }
        .md-callout {
          border-left: 3px solid #4f9cf9;
          background: rgba(79,156,249,0.06);
          border-radius: 0 10px 10px 0;
          padding: 12px 18px;
          margin: 16px 0;
        }
        .md-callout p { margin: 0; color: rgba(255,255,255,0.75); font-size: 13.5px; }
        .md-table-wrap { overflow-x: auto; margin: 16px 0 24px; border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; }
        .md-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .md-table th, .md-table td { padding: 10px 14px; text-align: left; border-bottom: 1px solid rgba(255,255,255,0.08); vertical-align: top; }
        .md-table th {
          background: rgba(79,156,249,0.1);
          color: #fff;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 600;
          font-size: 12.5px;
          letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .md-table td { color: rgba(255,255,255,0.75); line-height: 1.6; }
        .md-table tr:last-child td { border-bottom: none; }
        .md-table tr:hover td { background: rgba(255,255,255,0.02); }
        .md-image-frame {
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          overflow: hidden;
          background: rgba(255,255,255,0.02);
          margin: 16px 0 24px;
        }
        /* Inline-injected SVG diagrams (InlineSvgDiagram) — no card at all, transparent
           background (shows the page's own backdrop exactly, not a hardcoded color guess that
           can drift out of sync with it), so the diagram just sits directly on the page instead
           of looking boxed in. Any black text/lines in these Visio exports always carries its
           own light backing shape as part of the design itself (the user's own authoring
           convention), so there's no legibility risk from removing the card. The nested "svg"
           selector is what actually makes the injected markup responsive — stripSvgRootSize()
           removes the file's own literal inch-based width/height, so without this the browser
           would fall back to the UA default box (300×150px) instead of filling the container. */
        .md-inline-svg { margin: 16px 0 24px; padding: 12px 0; }
        .md-inline-svg svg { width: 100%; height: auto; display: block; }
        .md-inline-svg-wrap { position: relative; }
        .md-inline-svg-clickable { cursor: zoom-in; }
        /* Small, quiet affordance — invisible until the diagram is hovered, so it doesn't
           compete with the drawing itself, matching this app's general "no chrome until you
           reach for it" hover conventions (e.g. the BIM Roles download-item hover state). */
        .md-inline-svg-expand {
          position: absolute; top: 8px; right: 8px;
          width: 30px; height: 30px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(13,15,18,0.7); border: 1px solid rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.85); font-size: 15px;
          opacity: 0; transition: opacity 120ms ease, background 120ms ease;
        }
        .md-inline-svg-wrap:hover .md-inline-svg-expand { opacity: 1; }
        .md-inline-svg-expand:hover { background: #4f9cf9; border-color: #4f9cf9; color: #fff; }
        /* Touch has no hover state to reveal it with — stay visible below the app's usual
           mobile breakpoint instead of being undiscoverable. */
        @media (max-width: 768px) {
          .md-inline-svg-expand { opacity: 1; }
        }
        /* antd's Image wrapper div (.ant-image) defaults to display:inline-block — it sits on
           a text baseline and leaves a sliver of space below it that pokes out past this
           frame's rounded bottom corners. */
        .md-image-frame .ant-image { display: block; }
        .md-related-terms {
          display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
          margin: 4px 0 24px; padding: 8px 12px;
          background: rgba(79,156,249,0.05); border: 1px dashed rgba(79,156,249,0.25); border-radius: 8px;
        }
        .md-related-label {
          font-size: 12px; font-weight: 600; color: #7ab8ff; margin-right: 4px;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .md-related-tag {
          cursor: pointer; margin: 0; font-size: 12px; padding: 2px 10px;
          background: rgba(79,156,249,0.14); border-color: rgba(79,156,249,0.4); color: #9cccff;
          transition: background 120ms ease, color 120ms ease;
        }
        .md-related-tag:hover { background: #4f9cf9; color: #fff; border-color: #4f9cf9; }
        .md-discipline-links { display: flex; flex-wrap: wrap; gap: 8px; margin: 4px 0 24px; }
        .md-downloads { display: flex; flex-direction: column; gap: 10px; margin-top: 24px; }
        .md-download-item {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 16px; border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.02);
          text-decoration: none; transition: border-color 120ms ease, background 120ms ease;
        }
        .md-download-item:hover { border-color: rgba(79,156,249,0.4); background: rgba(79,156,249,0.06); }
        .md-download-icon { font-size: 20px; color: #e05252; flex-shrink: 0; }
        .md-download-name {
          flex: 1; min-width: 0; color: #fff; font-size: 13.5px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .md-download-size {
          font-family: 'DM Mono', monospace; font-size: 11px; color: rgba(255,255,255,0.4); flex-shrink: 0;
        }
        .md-download-action { color: #4f9cf9; flex-shrink: 0; }
      `}</style>

      <Image.PreviewGroup>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {processedText}
        </ReactMarkdown>

        {remainingImages.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 24, marginTop: 20 }}>
            {remainingImages.map((img) => {
              const src = imageUrlFor(img);
              const svg = isSvgSrc(src);
              return (
                <div key={img.name} style={{ width: widthFor(img.name), marginLeft: "auto", marginRight: "auto" }}>
                  {svg ? (
                    <InlineSvgDiagram src={src} dictionaryTerms={dictionaryTerms} onNavigateToDictionaryTerm={onNavigateToDictionaryTerm} />
                  ) : (
                    <div className="md-image-frame">
                      <Image src={src} alt={img.name} style={{ width: "100%", display: "block" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Image.PreviewGroup>

      {files.length > 0 && (
        <div className="md-downloads">
          {files.map((file) => (
            <a key={file.name} className="md-download-item" href={downloadUrlFor(file)} download>
              <FilePdfOutlined className="md-download-icon" />
              <span className="md-download-name">{file.name}</span>
              <span className="md-download-size">{file.sizeMB} MB</span>
              <DownloadOutlined className="md-download-action" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
